import { useEffect, useRef, useState } from 'react'
import config from '@config'

// Fail-safe: any failure in these checks must render the native login — never block login.

const BRANDING_TIMEOUT_MS = 4000

const fetchSsoConfig = async () => {
  const disabled = { enabled: false, verxBase: null }
  try {
    const pulseFeUrl = (config.PULSE_FE_URL || '').trim()
    if (!pulseFeUrl) {
      return disabled
    }

    const base = pulseFeUrl.replace(/\/+$/, '')
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), BRANDING_TIMEOUT_MS)

    try {
      const res = await fetch(`${base}/api/branding?client=${window.location.hostname}`, {
        method: 'GET',
        signal: controller.signal
      })
      if (res.ok) {
        const data = await res.json()
        if (data?.ssoLoginEnabled !== true) {
          return disabled
        }
        let verxBase = base
        if (data?.verxUrl && typeof data.verxUrl === 'string') {
          const host = data.verxUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '')
          if (host) {
            verxBase = `https://${host}`
          }
        }
        return { enabled: true, verxBase }
      }
      return disabled
    } finally {
      clearTimeout(timer)
    }
  } catch (err) {
    return disabled
  }
}

// Without ending the Auth0 session, the unified login silently signs the user back in.
export const ssoFederatedLogout = async () => {
  try {
    const { enabled, verxBase } = await fetchSsoConfig()
    if (!enabled) {
      return false
    }

    const domain = (config.AUTH0_DOMAIN || '').trim()
    const clientId = (config.AUTH0_CLIENT_ID || '').trim()
    if (!domain || !clientId) {
      return false
    }

    // Root path only: Auth0 logout-URL wildcards cannot carry a path.
    // ?loggedOut=1 stops the VerX SPA's refresh token from silently resurrecting the session.
    const destination = verxBase ? `${verxBase}/?loggedOut=1` : window.location.origin
    const returnTo = encodeURIComponent(destination)
    window.location.assign(`https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`)
    return true
  } catch (err) {
    // Fail-safe: never block logout on this check.
    console.warn('SSO federated logout skipped:', err)
    return false
  }
}

// `enabled` must stay false until auth settles with NO signed-in user — redirecting an authenticated visitor loops through the bridge.
const useSsoDomainRedirect = (enabled = true) => {
  const hasRun = useRef(false)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    if (!enabled) {
      return
    }
    if (hasRun.current) {
      return
    }
    hasRun.current = true

    const run = async () => {
      try {
        const { enabled, verxBase } = await fetchSsoConfig()
        if (enabled) {
          const base = verxBase
          const host = window.location.hostname
          // returnApp/returnHost is the contract the VerX side reads — do not rename.
          const target = `${base}/login?returnApp=alcatraz&returnHost=${encodeURIComponent(host)}`
          // Keep `checking` true while navigating away — avoids flashing the native form.
          window.location.assign(target)
          return
        }
        setChecking(false)
      } catch (err) {
        // Fail-safe: never block the native login on this check.
        console.warn('SSO domain redirect check skipped:', err)
        setChecking(false)
      }
    }

    run()
  }, [enabled])

  return checking
}

export default useSsoDomainRedirect
